/**
 * Composable for managing package deals in orders.
 *
 * Basic Rules:
 * - A package deal requires at least two pairs of glasses in the same order. Any two-glasses combination qualifies (TECH-723: correction is no longer required — two uncorrected optical glasses or two plano suns are eligible just like a corrected combination).
 * - If an influencer promotion is applied, the package deal switches to influencer mode 1% more discount. Keep the influencer promotion attached -> for BI tracking.
 *
 * Single Modes:
 * - Check if the customer has a previous order within the last 60 days that qualifies for a package deal.
 * - If such an order exists, link the current order to the previous one using attachments and apply the appropriate package deal tag.
 * - If an influencer promotion is applied, switch to influencer mode and update the order accordingly.
 *
 * On Checkout Page:
 * - Always Remove add Coupon codes from the order when applying a package deal. -> Fake exclusive (cannot be combined with other promotions other than influencer or sale)
 *
 * On Retro (Order Details Page):
 * - Package deal can be added on the same rules as mentioned above. Except we can't do the influencer promotion toggle here. As the retro discount is added as capture no modification of the order is possible.
 *
 * - Additional Rules for Retro:
 *   - If the current order already has a package deal applied, it should not be possible to add another one.
 *   - Only allow Sales promotions to be combined with retro package deals.
 *   - As soon a retro package deal is added, the user can not add retro promotions anymore.
 *
 * Technical Implementation:
 * - Order links are stored as attachments with reference_origin: '-link'
 * - The 'reference' field contains the linked order number for easy querying (e.g., filter[reference_eq]=12345)
 * - The 'metadata.role' field indicates whether the order is 'current' or 'origin'
 * - Influencer promotions use attachments with reference_origin: '--package-deal-influencer'
 */

// export const ENABLE_PD_REFUND = import.meta.env.VITE_WEB_ENV === 'test' // enable it on test first.
export const ENABLE_PD_REFUND = true

import { inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotification } from '@/composables/useNotification.js'
import { isMainProduct } from '@/utils/order.js'

export function packageDealPossible(currentOrder, lastCustomerOrder = null) {
  const lineItemOptions = lastCustomerOrder?.line_item_options || []
  const currentLensItems =
    currentOrder?.line_items?.filter((item) => item.metadata?.type === 'lens') || []

  for (const item of currentLensItems) {
    if (item.line_item_options && item.line_item_options.length > 0) {
      lineItemOptions.push(...item.line_item_options)
    }
  }

  // A single corrected pair (LENS2/LENS4) can still seed a single-mode package deal
  // (one pair in the cart linking to a previous order).
  const hasValidLensOption = lineItemOptions.some(
    (option) => option.name?.startsWith('LENS2::') || option.name?.startsWith('LENS4::')
  )

  // TECH-723: two or more pairs of glasses in the same cart qualify, regardless of
  // correction — including two uncorrected optical glasses or two plano suns.
  const validGlassesPairs = countPackageDealGlassesPairs(currentOrder)

  return hasValidLensOption || validGlassesPairs >= 2
}

/**
 * Count the pairs of glasses in an order that are valid package-deal candidates.
 *
 * Line items are grouped by their `reference` (product group). A group counts as a
 * valid pair when its main product matches one of these shapes:
 * - Corrected optical: main product + a lens option starting with LENS2:: or LENS4::
 * - Uncorrected optical: main product + a lens option starting with LENS1::
 * - Plano sun: a sun-type main product with no lens option in the group
 *
 * @param {Object} order - The order whose line items are inspected
 * @returns {number} - Number of valid package-deal glasses pairs
 */
export function countPackageDealGlassesPairs(order) {
  const groups = {}

  for (const lineItem of order?.line_items || []) {
    if (!lineItem.reference) {
      continue
    }

    if (!groups[lineItem.reference]) {
      groups[lineItem.reference] = { mainProduct: null, lensOptions: [] }
    }

    if (isMainProduct(lineItem)) {
      groups[lineItem.reference].mainProduct = lineItem
    }

    if (lineItem.metadata?.type === 'lens' && lineItem.line_item_options?.length > 0) {
      groups[lineItem.reference].lensOptions.push(...lineItem.line_item_options)
    }
  }

  return Object.values(groups).filter((group) => {
    if (!group.mainProduct) {
      return false
    }

    const optionNames = group.lensOptions.map((option) => option.name || '')
    const isCorrectedOptical = optionNames.some(
      (name) => name.startsWith('LENS2::') || name.startsWith('LENS4::')
    )
    const isUncorrectedOptical = optionNames.some((name) => name.startsWith('LENS1::'))
    const isPlanoSun = group.mainProduct.metadata?.type === 'sun' && group.lensOptions.length === 0

    return isCorrectedOptical || isUncorrectedOptical || isPlanoSun
  }).length
}

export function usePackageDeal() {
  const { t } = useI18n()
  const cl = inject('cl')
  const Sentry = inject('Sentry')

  const { showNotification } = useNotification()

  const PACKAGE_DEAL_INFLUENCER_TAG = 'package-deal-influencer'
  const PACKAGE_DEAL_TAG = 'package-deal'
  // Promotions with this tag combine with a package deal, regardless of viu_category metadata
  const PACKAGE_DEAL_ALLOWED_TAG = 'package-deal-allowed'
  const MAX_DAYS_BACK = 60
  const VALID_PAYMENT_STATUSES = [
    'paid',
    'partially_paid',
    'partially_refunded',
    'authorized',
    'free',
    'unpaid'
  ]
  const VALID_FULFILLMENT_STATUSES = ['fulfilled', 'in_progress']
  const ALLOWED_PROMOTION_CATEGORY_MATCHES = ['influencer', 'sale', 'kickback', 'pd-allowed']
  const ALLOWED_RETRO_PROMOTIONS = ['goodwill', 'kickback', 'sale']
  const PACKAGE_DEAL_LENS_ITEMS = ['LENS2', 'LENS4'] // Two or more pairs of glasses in the same basket (at least 1 with correction (LENS2 or LENS4)
  const PACKAGE_DEAL_ADDITIONAL_LENS_ITEMS = ['LENS1', 'LENS3'] // Additional pairs of glasses without correction

  const CUSTOMER_LAST_ORDERS_INCLUDES = [
    'line_items.item',
    'line_items.line_item_options.sku_option',
    'shipments',
    'shipments.line_items.item',
    'shipments.line_items.return_line_items.return',
    'shipments.line_items.line_item_options.sku_option',
    'shipments.tags',
    'shipments.shipping_method',
    'customer',
    'market',
    'tags',
    'attachments',
    'captures'
  ]

  const CUSTOMER_LAST_ORDERS_FIELDS = {
    orders: [
      'status',
      'number',
      'approved_at',
      'placed_at',
      'metadata',
      'total_amount_float',
      'payment_status',
      'shipments',
      'line_items',
      'fulfillment_status',
      'currency_code',
      'customer',
      'market',
      'tags',
      'attachments',
      'order_copies',
      'captures'
    ],
    line_items: [
      'sku_code',
      'item_type',
      'image_url',
      'metadata',
      'item',
      'reference',
      'return_line_items',
      'line_item_options'
    ],
    shipments: [
      'line_items',
      'status',
      'number',
      'tags',
      'shipping_method',
      'metadata',
      'reference_origin'
    ],
    shipping_method: ['reference'],
    customers: ['metadata'],
    markets: ['code'],
    skus: ['metadata'],
    return_line_items: ['return'],
    returns: ['reference', 'status'],
    attachments: ['reference_origin']
  }

  const INFLUENCER_PACKAGE_DEAL_REFERENCE_ORIGIN = '--package-deal-influencer'
  const PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN = '--package-deal-order'

  // ============================================================================
  // HELPER FUNCTIONS - Filters & Basic Checks
  // ============================================================================

  const influencerFilter = (lineItem) => lineItem?.name?.includes('Influencer')

  function packageDealApplied(order) {
    const discountLineItem =
      getPackageDealDiscountLineItem(order) || getRetroPackageDealCapture(order)
    return Boolean(discountLineItem?.id)
  }

  /**
   * Do calculate package deal adjustment amount$
   * - As long it's a partial return in which the latest return is a package deal return
   */
  function doCalculatePackageDealAdjustmentAmount(
    order,
    checkForPackageDealRefundCompleted = true
  ) {
    if (!ENABLE_PD_REFUND) {
      return false
    }
    const latestReturn = order.returns
      ?.filter((ret) => ret.status === 'received')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

    const hasPackageDealReturn = Boolean(
      latestReturn?.metadata?.packageDealRefundData?.packageDealAdjustmentAmount
    )

    // also check if return has a refund
    const hasARefund = order.transactions?.some((tx) => {
      return (
        tx.succeeded && tx.type === 'refunds' && tx.metadata?.return_number === latestReturn.number
      )
    })

    // return completed
    const returnCompleted = latestReturn?.tags?.some((tag) => tag.name === 'refunded')

    // needed if return amount is split over multiple refunds
    if (hasARefund && !returnCompleted) {
      return false
    }

    if (checkForPackageDealRefundCompleted) {
      return hasPackageDealReturn && returnCompleted
    }

    return hasPackageDealReturn
  }

  /**
   * Checks if a package deal has been cancelled.
   * @param {*} order
   * @returns
   */
  function packageDealCancelled(order) {
    const packageDealReturn = order.returns?.find(
      (ret) => ret.metadata?.packageDealRefundData?.packageDealAdjustmentAmount
    )

    // either part of it is refunded or we have a specific tag for it
    return (
      Boolean(packageDealReturn) &&
      (packageDealReturn.tags?.some((tag) => tag.name === 'refunded') ||
        order.transactions?.some((tx) => {
          return (
            tx.succeeded &&
            tx.type === 'refunds' &&
            tx.metadata?.return_number === packageDealReturn.number &&
            tx.succeeded
          )
        }))
    )
  }

  // ============================================================================
  // HELPER FUNCTIONS - Data Extraction
  // ============================================================================

  function getInfluencerPackageDealItem(currentOrder) {
    return currentOrder?.attachments?.find(
      (att) => att.reference_origin === INFLUENCER_PACKAGE_DEAL_REFERENCE_ORIGIN
    )
  }

  function getPackageDealOrderLink(order) {
    return order?.attachments?.find((att) =>
      att.reference_origin.startsWith(PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN)
    )
  }

  function getPromotionLineItem(currentOrder) {
    return currentOrder?.line_items?.find((item) => item.item_type.includes('_promotions'))
  }

  function getPackageDealDiscountLineItem(order) {
    const promotionLineItem = getPromotionLineItem(order)

    if (
      promotionLineItem &&
      !promotionLineItem.item?.name &&
      promotionLineItem?.name?.includes('Package Deal')
    ) {
      return promotionLineItem
    } else if (promotionLineItem && promotionLineItem.item?.reference?.startsWith('PACKAGE_DEAL')) {
      return promotionLineItem
    }
  }

  function getRetroPackageDealCapture(order) {
    const captures = order?.captures ? order.captures : order?.transactions || []
    return captures.find((tx) => {
      return (
        tx.succeeded &&
        tx.metadata &&
        tx.metadata.discount_breakdown &&
        tx.metadata.discount_promotion.includes('Package Deal') &&
        tx.type === 'captures'
      )
    })
  }

  function getPackageDealRefund(order) {
    return order?.transactions?.find((tx) => {
      return (
        tx.succeeded &&
        tx.metadata &&
        tx.metadata.promotion_details &&
        tx.metadata.promotion_details.type === 'package_deal' &&
        tx.type === 'refunds'
      )
    })
  }

  function getPackageDealName(discountItem, lineItems) {
    const packageDealItem = lineItems.find((item) => item?.item?.id === discountItem.id)
    const activeRule = packageDealItem?.rule_outcomes?.find((rule) => rule.match)
    return activeRule ? activeRule?.name : packageDealItem?.name || discountItem.name
  }

  /** only return if the package deal return is not completed yet */
  function getPackageDealStornoAmount(order) {
    if (doCalculatePackageDealAdjustmentAmount(order, false)) {
      return order?.returns
        ?.filter((ret) => ret.metadata?.packageDealRefundData?.packageDealAdjustmentAmount)
        .reduce((sum, ret) => {
          return sum + (ret.metadata.packageDealRefundData?.packageDealAdjustmentAmount || 0)
        }, 0)
    }

    return 0
  }

  // ============================================================================
  // PROMOTION VALIDATION
  // ============================================================================

  function hasPackageDealAllowedTag(promotion) {
    return Boolean(promotion?.tags?.some((tag) => tag.name === PACKAGE_DEAL_ALLOWED_TAG))
  }

  // Loads the promotion tags when the caller fetched the promotion without them
  async function withPromotionTags(promotion) {
    if (!promotion?.id || promotion.tags) return promotion
    try {
      const { tags } = await cl.promotions.retrieve(promotion.id, { include: ['tags'] })
      return { ...promotion, tags }
    } catch (error) {
      Sentry.captureException(error)
      return promotion
    }
  }

  function isAllowedRetroPromotion(promotion, couponCode = '') {
    if (hasPackageDealAllowedTag(promotion)) return true
    return ALLOWED_RETRO_PROMOTIONS.some((category) => {
      if (category === 'kickback') {
        return couponCode?.toUpperCase()?.startsWith('KICK')
      }
      return promotion?.metadata?.viu_category?.toLowerCase()?.includes(category)
    })
  }

  function isAllowedPromotion(promotion, isRetro = false) {
    if (hasPackageDealAllowedTag(promotion)) return true

    let allowedPromotionMatches = ALLOWED_PROMOTION_CATEGORY_MATCHES

    // only allow sales promotions
    if (isRetro) {
      allowedPromotionMatches = ALLOWED_PROMOTION_CATEGORY_MATCHES.filter(
        (match) => match === 'sale'
      )
    }

    return allowedPromotionMatches.some((match) => {
      return promotion?.metadata?.viu_category?.toLowerCase()?.includes(match)
    })
  }

  async function getAllowedPromotion(couponObject, isRetro = false) {
    if (!couponObject.promotion_rule?.promotion?.name) {
      // check if promotion name matches allowed pattern
      const coupons = await cl.coupons.list({
        filters: {
          code_eq: couponObject?.code?.toUpperCase()
        },
        include: ['promotion_rule.promotion.tags']
      })

      if (coupons?.length > 0) {
        couponObject = coupons[0]
      }
    }

    const promotion = await withPromotionTags(couponObject.promotion_rule?.promotion)
    if (isAllowedPromotion(promotion, isRetro)) {
      return couponObject
    }

    return null
  }

  // ============================================================================
  // TAG MANAGEMENT
  // ============================================================================

  /**
   * Get the appropriate tag based on mode
   */
  function getTagToAddForMode(mode) {
    return isInfluencerMode(mode) ? PACKAGE_DEAL_INFLUENCER_TAG : PACKAGE_DEAL_TAG
  }

  // ============================================================================
  // INFLUENCER PROMOTION MANAGEMENT
  // ============================================================================

  async function removeInfluencerPackageDealItem(currentOrder) {
    const influencerPackageDealItem = getInfluencerPackageDealItem(currentOrder)

    if (influencerPackageDealItem?.id) {
      await cl.attachments.delete(influencerPackageDealItem.id)
    }
  }

  async function createPackageDealOrderLink(currentOrder, linkedOrder, role) {
    let name = ''
    let reference_origin = ''
    let orderNumber = ''
    const metadata = {
      role: role
    }

    if (role === 'current') {
      orderNumber = currentOrder.number
      reference_origin = PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN
      name = `Package Deal: Linked to Origin Order ${linkedOrder.number}`
      metadata.linked_order_id = linkedOrder.id
      metadata.linked_order_number = linkedOrder.number
    } else if (role === 'origin') {
      orderNumber = linkedOrder.number
      reference_origin = `${PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN}-linked`
      name = `Package Deal: Used in Order ${linkedOrder.number}`
      metadata.used_in_order_id = linkedOrder.id
      metadata.used_in_order_number = linkedOrder.number
    }

    return await cl.attachments.create({
      reference_origin: reference_origin,
      reference: orderNumber,
      name: name,
      metadata: metadata,
      attachable: cl.orders.relationship(currentOrder.id)
    })
  }

  async function updateInfluencerPromotion(currentOrder, influencerCoupon) {
    showNotification({
      message: t('package_deal.influencer_package_deal_applied'),
      variant: 'info'
    })

    return await cl.attachments.create({
      reference_origin: '--package-deal-influencer',
      reference: currentOrder.number,
      name: 'Package Deal Influencer Promotion: ' + currentOrder.number,
      metadata: {
        influencer_promotion: {
          discount_promotion: influencerCoupon.promotion_rule.promotion.name,
          discount_promotion_id: influencerCoupon.promotion_rule.promotion.id,
          discount_code: influencerCoupon.code,
          discount_type: influencerCoupon.promotion_rule.promotion.type
        }
      },
      attachable: cl.orders.relationship(currentOrder.id)
    })
  }

  /**
   * Build a virtual discount line item for influencer package deals
   * This is used to display the discount in the cart UI
   */
  function buildVirtualDiscountLineItem(currentOrder) {
    const hasPackageDeal = packageDealApplied(currentOrder)
    const packageDealLineItem = getInfluencerPackageDealItem(currentOrder)
    if (!packageDealLineItem?.metadata?.influencer_promotion || !hasPackageDeal) return null

    try {
      const influencerPromo = packageDealLineItem.metadata.influencer_promotion
      // now create a fictive discount line item to show in the cart UI
      const virtualDiscountLineItem = {
        id: `virtual-discount-${influencerPromo.discount_code}`,
        reference: `virtual-discount-${influencerPromo.discount_code}`,
        item_type: influencerPromo.discount_type,
        coupon_code: influencerPromo.discount_code,
        item: {
          id: influencerPromo.discount_promotion_id,
          name: influencerPromo.discount_promotion
        },
        currency_code: currentOrder.currency_code
      }

      return virtualDiscountLineItem
    } catch (error) {
      return null
    }
  }

  /**
   * Toggle package deal when promotions are applied/removed
   * If there is already an influencer package deal applied, toggle it off if it's not the influencer coupon anymore
   */
  async function promotionPackageDealToggle(currentOrder, coupon = null) {
    const result = {
      success: true,
      errorMessage: null,
      tagToAdd: null
    }
    let tagToAdd = null
    let allowedCoupon = null

    await removeInfluencerPackageDealItem(currentOrder)

    if (coupon !== null) {
      allowedCoupon = await getAllowedPromotion(coupon)

      if (!allowedCoupon) {
        result.success = false
        result.errorMessage = t('package_deal.coupon_not_allowed_with_package_deal', {
          coupon: coupon.code
        })
        return result
      }
    }

    // hasPackageDeal if we have no influencer tags or PACKAGE_DEAL_TAG
    const hasPackageDeal = packageDealApplied(currentOrder)

    const isInfluencerCoupon = allowedCoupon?.promotion_rule?.promotion?.metadata?.viu_category
      ?.toLowerCase()
      ?.includes('influencer')

    if (hasPackageDeal && allowedCoupon && isInfluencerCoupon) {
      tagToAdd = PACKAGE_DEAL_INFLUENCER_TAG
    } else if (hasPackageDeal) {
      tagToAdd = PACKAGE_DEAL_TAG
    }

    await handlePackageDealOnOrder(
      { id: currentOrder.id, tags: currentOrder.tags },
      isInfluencerCoupon ? allowedCoupon : null,
      tagToAdd
    )

    return {
      success: true,
      errorMessage: null,
      tagToAdd: tagToAdd,
      applyCouponCode: allowedCoupon !== null && !isInfluencerCoupon
    }
  }

  async function refreshOrder(currentOrderUpdates) {
    delete currentOrderUpdates.tags
    return await cl.orders.update(
      {
        ...currentOrderUpdates,
        _refresh: true
      },
      { include: ['line_items', 'tags'] }
    )
  }

  // ============================================================================
  // ORDER UPDATE OPERATIONS
  // ============================================================================

  async function updateOrderTags(currentOrderUpdates, tagToAdd = null) {
    // remove existing package deal tags first
    let updatedOrder = null
    if (
      currentOrderUpdates.tags?.some((tag) => {
        return [PACKAGE_DEAL_TAG].includes(tag.name)
      })
    ) {
      updatedOrder = await cl.orders._remove_tags(currentOrderUpdates.id, PACKAGE_DEAL_TAG)
    }

    if (
      currentOrderUpdates.tags?.some((tag) => {
        return [PACKAGE_DEAL_INFLUENCER_TAG].includes(tag.name)
      })
    ) {
      updatedOrder = await cl.orders._remove_tags(
        currentOrderUpdates.id,
        PACKAGE_DEAL_INFLUENCER_TAG
      )
    }

    if (updatedOrder?.id) {
      updatedOrder = await refreshOrder(currentOrderUpdates)
    }

    // it's important to have a clean state before adding tags, otherwise we might run into issues with concurrent updates where tags are added twice
    if (tagToAdd) {
      updatedOrder = await cl.orders._add_tags(currentOrderUpdates.id, tagToAdd, {
        include: ['line_items', 'tags']
      })

      updatedOrder = await refreshOrder(currentOrderUpdates)
    }

    return updatedOrder
  }

  /**
   * Core function to handle package deal operations on orders
   * Updates tags and influencer promotions as needed
   */
  async function handlePackageDealOnOrder(
    currentOrderUpdates,
    influencerCoupon = null,
    tagToAdd = null
  ) {
    try {
      const isInfluencer = influencerCoupon !== null
      const updatedOrder = await updateOrderTags(currentOrderUpdates, tagToAdd)

      if (isInfluencer) {
        return await updateInfluencerPromotion(updatedOrder, influencerCoupon)
      }

      return updatedOrder
    } catch (error) {
      Sentry.setContext('package-deal-error', {
        currentOrderUpdates,
        influencerCoupon
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('handlePackageDealOnOrderFailed')
        return scope
      })
    }
  }

  // ============================================================================
  // CUSTOMER ORDER RETRIEVAL
  // ============================================================================

  function getMaxDaysBackDate() {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    date.setDate(date.getDate() - MAX_DAYS_BACK)
    return date
  }

  async function getCustomerLastOrders(
    customerId,
    countryCode = null,
    onOrderDetails = true,
    pageSize = 10
  ) {
    const orderMustBeNewerThanDate = getMaxDaysBackDate()
    const orders = await cl.customers.orders(customerId, {
      filters: {
        market_merchant_address_country_code_eq: countryCode,
        payment_status_in: VALID_PAYMENT_STATUSES,
        approved_at_gteq: `${orderMustBeNewerThanDate.toISOString()}`, // 60 days ago
        fulfillment_status_in: VALID_FULFILLMENT_STATUSES,
        status_eq: 'approved',
        line_items_reference_not_null: true
      },
      include: CUSTOMER_LAST_ORDERS_INCLUDES,
      fields: CUSTOMER_LAST_ORDERS_FIELDS,
      pageSize: pageSize,
      sort: { approved_at: 'desc' }
    })

    return orders
      .filter((order) => orderIsEligibleForPackageDeal(order, onOrderDetails, false).success)
      .filter((order) => {
        const hasPdTag = order.tags?.some((tag) =>
          [PACKAGE_DEAL_TAG, PACKAGE_DEAL_INFLUENCER_TAG].includes(tag.name)
        )
        const hasPdAttachment = Boolean(getPackageDealOrderLink(order))
        return !(hasPdTag && hasPdAttachment)
      })
  }

  async function getCustomerLastOrder(customerId, countryCode = null) {
    // get the last order of the customer thats not older than 60 days (midnight to midnight)
    const lastOrder = await getCustomerLastOrders(customerId, countryCode, 1)

    return lastOrder && lastOrder.length > 0 ? lastOrder[0] : null
  }

  // ============================================================================
  // PACKAGE DEAL APPLICATION & REMOVAL
  // ============================================================================

  /**
   * Apply retro package deal (single mode)
   * Links current order to an origin order using attachments and applies appropriate tags
   */
  async function setPackageDealOrderLinks(
    currentOrder,
    originOrder,
    influencerPromotion,
    tagToAdd
  ) {
    try {
      // Create link attachment on current order pointing to origin (always)
      await createPackageDealOrderLink(currentOrder, originOrder, 'current')

      // For already-approved orders (retro/order details flow), stamp the reverse link immediately.
      // For cart orders (not yet approved), the reverse link is created at approval time via
      // freezePackageDealLinkOnApprove, preventing stale links from abandoned carts.
      if (currentOrder.status === 'approved') {
        await createPackageDealOrderLink(originOrder, currentOrder, 'origin')
      }

      return await handlePackageDealOnOrder(
        { id: currentOrder.id, tags: currentOrder.tags },
        influencerPromotion,
        tagToAdd
      )
    } catch (error) {
      await removePackageDeal({ id: currentOrder.id }, true)
      Sentry.setContext('package-deal-error', {
        currentOrderId: currentOrder.id,
        originOrderId: originOrder.id
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('setPackageDealOrderLinksFailed')
        return scope
      })

      throw error
    }
  }

  async function removeAllPackageDealAttachments(order) {
    const attachmentsToRemove = []
    const orderLink = getPackageDealOrderLink(order)

    // Remove linked order attachment if present
    if (orderLink?.id) {
      const attachments = await cl.attachments.list({
        filters: {
          reference_eq: order.number,
          reference_origin_start: PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN,
          metadata_jcont: { role: 'origin' }
        }
      })

      attachmentsToRemove.push(...attachments)
    }
    attachmentsToRemove.push(
      ...(order?.attachments.filter(
        (att) =>
          att.reference_origin.startsWith(PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN) ||
          att.reference_origin === INFLUENCER_PACKAGE_DEAL_REFERENCE_ORIGIN
      ) || [])
    )

    await Promise.all(
      attachmentsToRemove.map(async (attachment) => {
        if (attachment?.id) {
          try {
            await cl.attachments.delete(attachment.id)
          } catch (_error) {
            // Ignore errors during attachment deletion
          }
        }
      })
    )
  }

  // ============================================================================
  // ELIGIBILITY CHECKS & UI STATE
  // ============================================================================

  /**
   * Check if an order is eligible for package deal based on:
   * - Payment status (only on order details)
   * - Has eligible main products
   * - No returns on eligible items
   * - Has no existing package deal (only on order details)
   *
   * @param {Object} order - The order to validate
   * @param {boolean} onOrderDetails - Whether validation is from order details page
   * @returns {Object} - { success: boolean, errorKey?: string, errorDetails?: object }
   */
  function orderIsEligibleForPackageDeal(
    order,
    onOrderDetails = false,
    checkForPackageDealApplied = true
  ) {
    try {
      if (['cancelled'].includes(order.status)) {
        return {
          success: false,
          errorKey: 'order_not_in_valid_status',
          errorDetails: { status: order.status }
        }
      }

      // guest check
      // flag only as guest if guest is true and customer_email like vcp+guest%
      if (order.guest && order.customer_email?.startsWith('vcp+guest')) {
        return {
          success: false,
          errorKey: 'guest_checkout_not_eligible'
        }
      }

      // check mode first
      const mode = getMode(order)
      if (!mode) {
        return {
          success: false,
          errorKey: 'line_items_do_not_meet_package_deal_requirements'
        }
      }

      // check if any of the line items are returned
      // allow returnItem.return with reference intolerance or insurance
      // const allowedReturnReferences = ['intolerance', 'insurance']
      // const hasReturnedItems = order.line_items?.some(
      //   (item) =>
      //     item.return_line_items &&
      //     item.return_line_items.some(
      //       (returnItem) => !allowedReturnReferences.includes(returnItem.return?.reference)
      //     )
      // )

      // console.log('hasReturnedItems', hasReturnedItems)
      // if (hasReturnedItems) {
      //   return {
      //     success: false,
      //     errorKey: 'order_contains_returned_items'
      //   }
      // }

      // check if it is an insurance or intolerance order
      const isReplacementOrder = ['insurance', 'intolerance'].includes(
        order.metadata?.card_source_type
      )
      if (isReplacementOrder) {
        return {
          success: false,
          errorKey: 'replacement_order_not_eligible'
        }
      }

      // Payment status check (only on order details)
      if (onOrderDetails) {
        if (
          order.payment_status !== 'unpaid' &&
          !VALID_PAYMENT_STATUSES.includes(order.payment_status)
        ) {
          return {
            success: false,
            errorKey: 'invalid_payment_status',
            errorDetails: { status: order.payment_status }
          }
        }
      }

      // Check for existing package deal (only on order details)
      if (onOrderDetails) {
        const orderLink = getPackageDealOrderLink(order)
        if (orderLink) {
          return {
            success: false,
            errorKey: 'already_has_package_deal',
            errorDetails: { orderLinkNumber: orderLink?.reference || null }
          }
        }
        if (checkForPackageDealApplied && packageDealApplied(order)) {
          return {
            success: false,
            errorKey: 'discount_already_applied'
          }
        }
      }
      return { success: true }
    } catch (error) {
      Sentry.setContext('package-deal-error', {
        orderId: order.id
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('orderIsEligibleForPackageDealFailed')
        return scope
      })
      return {
        success: false,
        errorKey: 'validation_error'
      }
    }
  }

  /**
   * A package deal can be canceled if after removing the selected return items,
   * the order is no longer eligible for a package deal and payment status is 'paid'.
   *
   * @param {*} order
   * @param {*} selectedReturnItems
   * @returns
   */
  function packageDealCanBeCanceled(order, selectedReturnItems = []) {
    // check if already refunded
    const packageDealRefund = getPackageDealRefund(order)
    if (packageDealRefund) {
      return false
    }

    // remove all selected return items from the order line items and check if the remaining items are still eligible for package deal
    order.line_items = order.line_items.filter((lineItem) => {
      return !selectedReturnItems.some((returnItem) => returnItem.line_item_id === lineItem.id)
    })

    // if not eligible anymore, we can cancel the package deal
    const eligibilityResult = orderIsEligibleForPackageDeal(order, true)
    return (
      eligibilityResult.success === false &&
      ['paid', 'partially_refunded'].includes(order.payment_status)
    )
  }

  /**
   * Check if the order is eligible for a package deal.
   * @param {Object} currentOrder - The current order
   * @param {boolean} onOrderDetailsPage - Whether check is from order details page
   * @returns {boolean}
   */
  function showPackageDealDialog(currentOrder, onOrderDetailsPage = false) {
    if (!currentOrder) {
      return false
    }

    // on order details only show if payment status is valid
    if (onOrderDetailsPage) {
      const orderPaid = ['free', 'paid', 'partially_refunded']
      if (orderPaid.includes(currentOrder.payment_status)) {
        return false
      }
    }

    // if current order is fulfilled, no package deal possible
    if (currentOrder.fulfillment_status === 'fulfilled') {
      return false
    }

    // First check basic eligibility
    const eligibilityResult = orderIsEligibleForPackageDeal(currentOrder, onOrderDetailsPage)

    if (!eligibilityResult.success) {
      return false
    }

    // Then check if order has a valid package deal mode
    const mode = getMode(currentOrder)
    if (!mode) {
      return false
    }

    return true
  }

  function showRemovePackageDealButton(currentOrder) {
    if (!currentOrder) {
      return false
    }

    return packageDealApplied(currentOrder)
  }

  async function getPackageDealPromotionRule() {
    const promotionRule = await cl.flex_promotions.list({
      filters: {
        reference_eq: 'PACKAGE_DEAL'
      },
      pageSize: 1
    })

    return promotionRule.length > 0 ? promotionRule[0] : null
  }

  // ============================================================================
  // MODE DETECTION & PRODUCT CATEGORIZATION
  // ============================================================================

  /**
   * Package deal modes
   *
   * Regular modes: Order has 2+ product groups, at least one with correction
   * Single modes: Order has only 1 product group (needs to link to another order)
   *   - single_with_correction: Has 1 frame with correction (LENS2/LENS4)
   *   - single_without_correction: Has 1 frame without correction (sun/LENS1/LENS3)
   * Influencer variants: Same as above but with influencer promotion applied
   */
  const MODES = {
    REGULAR: 'regular',
    REGULAR_INFLUENCER: 'regular_influencer',
    SINGLE_WITH_CORRECTION: 'single_with_correction',
    SINGLE_WITHOUT_CORRECTION: 'single_without_correction',
    SINGLE_WITH_CORRECTION_INFLUENCER: 'single_with_correction_influencer',
    SINGLE_WITHOUT_CORRECTION_INFLUENCER: 'single_without_correction_influencer'
  }

  /**
   * Helper function to check if a mode is a single mode (any variant)
   * @param {string} mode - The mode to check
   * @returns {boolean} - True if mode is any single variant
   */
  function isSingleMode(mode) {
    return Boolean(mode?.startsWith('single_'))
  }

  function isSingleWithoutCorrectionMode(mode) {
    return Boolean(
      mode === MODES.SINGLE_WITHOUT_CORRECTION ||
        mode === MODES.SINGLE_WITHOUT_CORRECTION_INFLUENCER
    )
  }

  function isSingleWithCorrectionMode(mode) {
    return Boolean(
      mode === MODES.SINGLE_WITH_CORRECTION || mode === MODES.SINGLE_WITH_CORRECTION_INFLUENCER
    )
  }

  /**
   * Helper function to check if a mode is a regular mode (any variant)
   * @param {string} mode - The mode to check
   * @returns {boolean} - True if mode is any regular variant
   */
  function isRegularMode(mode) {
    return Boolean(mode?.startsWith(MODES.REGULAR))
  }

  /**
   * Helper function to check if a mode has influencer promotion
   * @param {string} mode - The mode to check
   * @returns {boolean} - True if mode is an influencer variant
   */
  function isInfluencerMode(mode) {
    return Boolean(mode?.endsWith('_influencer'))
  }

  /**
   * Determine the package deal mode based on order state
   *
   * Product group specification:
   * - A Product Group always starts with '0' in the reference of the main product.
   *
   * - Frame without correction: Main product is sun or (isMainProduct and has Lens option LENS1 or LENS3 present)
   * - Frame with correction: isMainProduct and has Lens option LENS2 or LENS4 present
   *
   * Rules:
   * - Single modes: Order has exactly 1 product group
   *   - single_with_correction: 1 frame with correction (can link to any eligible order)
   *   - single_without_correction: 1 frame without correction (must link to order with correction)
   * - Regular mode: Has 2+ product groups (TECH-723: correction no longer required —
   *   any two-glasses combination qualifies, including two uncorrected or two plano suns)
   *
   * @param {Object} currentOrder - The order to analyze
   * @returns {string|null} - Mode string from MODES constant, or null if no valid mode
   */
  function getMode(currentOrder) {
    const productGroups = categorizeProductGroups(currentOrder)
    const hasInfluencer = currentOrder.line_items?.some(influencerFilter)

    const groupCounts = Object.values(productGroups).reduce(
      (counts, category) => {
        if (category === 'frame_without_correction') {
          counts.withoutCorrection++
        } else if (category === 'frame_with_correction') {
          counts.withCorrection++
        }
        return counts
      },
      { withoutCorrection: 0, withCorrection: 0 }
    )

    const { withoutCorrection, withCorrection } = groupCounts
    const totalGroups = withoutCorrection + withCorrection

    // Single mode: Exactly one product group
    if (totalGroups > 0) {
      if (withCorrection === 1 && withoutCorrection === 0) {
        // Single frame with correction
        return hasInfluencer
          ? MODES.SINGLE_WITH_CORRECTION_INFLUENCER
          : MODES.SINGLE_WITH_CORRECTION
      } else if (withCorrection === 0 && withoutCorrection === 1) {
        // Single frame without correction
        return hasInfluencer
          ? MODES.SINGLE_WITHOUT_CORRECTION_INFLUENCER
          : MODES.SINGLE_WITHOUT_CORRECTION
      }
    }

    // Regular mode: any two (or more) product groups, regardless of correction.
    // TECH-723: correction is no longer required — two uncorrected optical glasses
    // or two plano suns now qualify just like a corrected combination.
    const isRegular = totalGroups >= 2

    if (isRegular) {
      return hasInfluencer ? MODES.REGULAR_INFLUENCER : MODES.REGULAR
    }

    return null
  }

  /**
   * Categorize product groups based on their lens options
   *
   * @param {Object} order - The order object containing line_items
   * @returns {Object} - Object where key is product group reference and value is category:
   *                     'frame_without_correction', 'frame_with_correction', or 'other'
   *
   * Categories:
   * - 'frame_without_correction': Sun glasses or frames with LENS1, LENS3
   * - 'frame_with_correction': Frames with LENS2 or LENS4 (prescription lenses)
   * - 'other': Products that don't match above categories
   */
  function categorizeProductGroups(order) {
    const productGroups = {}

    if (!order?.line_items) {
      return productGroups
    }

    // Group line items by their reference (product group)
    const groupedItems = {}

    for (const lineItem of order.line_items) {
      // Skip items without reference
      if (!lineItem.reference) {
        continue
      }

      const reference = lineItem.reference

      if (!groupedItems[reference]) {
        groupedItems[reference] = {
          mainProduct: null,
          lensItems: [],
          allItems: []
        }
      }

      groupedItems[reference].allItems.push(lineItem)

      // Identify main product
      if (isMainProduct(lineItem)) {
        groupedItems[reference].mainProduct = lineItem
      }

      // Identify lens items
      if (lineItem.metadata?.type === 'lens') {
        groupedItems[reference].lensItems.push(lineItem)
      }
    }

    // Categorize each product group
    for (const [reference, group] of Object.entries(groupedItems)) {
      const { mainProduct, lensItems } = group

      // Must have a main product to categorize
      if (!mainProduct) {
        productGroups[reference] = 'other'
        continue
      }

      // Check if it's a sun glass
      const isSunGlass = mainProduct.metadata?.type === 'sun'

      // Collect all lens options from lens items
      const lensOptions = []
      for (const lensItem of lensItems) {
        if (lensItem.line_item_options && lensItem.line_item_options.length > 0) {
          lensOptions.push(...lensItem.line_item_options)
        }
      }

      // Check for specific lens option types use PACKAGE_DEAL_LENS_ITEMS as startsWith checks
      const hasLensWithCorrection = lensOptions.some((opt) =>
        PACKAGE_DEAL_LENS_ITEMS.some((lensType) => opt.name?.startsWith(`${lensType}::`))
      )

      const hasLensWithoutCorrection = lensOptions.some((opt) =>
        PACKAGE_DEAL_ADDITIONAL_LENS_ITEMS.some((lensType) => opt.name?.startsWith(`${lensType}::`))
      )

      // Apply categorization rules
      if (hasLensWithCorrection) {
        // Frame with correction (prescription)
        productGroups[reference] = 'frame_with_correction'
      } else if (isSunGlass || hasLensWithoutCorrection) {
        // Frame without correction
        productGroups[reference] = 'frame_without_correction'
      } else {
        // Doesn't match known patterns
        productGroups[reference] = 'other'
      }
    }

    return productGroups
  }

  // ============================================================================
  // VALIDATION SYSTEM
  // ============================================================================

  /**
   * Package Deal Validation Rules
   * Each rule defines:
   * - key: unique identifier
   * - label: i18n key for the validation message shown to user
   * - priority: execution order (lower = higher priority)
   * - modes: array of modes this validation applies to (empty = all modes)
   * - entryModes: array of entry modes this validation applies to (empty = all entry modes)
   *   Entry modes: 'checkout' | 'orderDetails'
   * - validate: async function that returns { success: boolean, errorKey?: string, errorDetails?: object }
   * - errorMessages: object mapping errorKeys to i18n keys
   */
  const getValidationRules = () => [
    {
      key: 'viu_conscious_club',
      label: 'package_deal.validation.viu_conscious_club.label',
      priority: 1,
      modes: [],
      entryModes: [],
      validate: async (_order, newsletterChoice) => {
        if (newsletterChoice !== true) {
          return { success: false, errorKey: 'not_in_viu_conscious_club' }
        }
        return { success: true }
      },
      errorMessages: {
        not_in_viu_conscious_club:
          'package_deal.validation.viu_conscious_club.not_in_viu_conscious_club'
      }
    },
    {
      key: 'allowed_promotions',
      label: 'package_deal.validation.allowed_promotions.label',
      priority: 2,
      modes: [],
      entryModes: [],
      validate: async (_order, _newsletterChoice, promotionLineItem, onOrderDetails) => {
        const promotion = await withPromotionTags(promotionLineItem)
        if (promotion && isAllowedPromotion(promotion, onOrderDetails) !== true) {
          return {
            success: false,
            errorKey: 'not_in_allowed_promotions',
            errorDetails: { promotion: promotionLineItem?.name || null }
          }
        }
        return { success: true }
      },
      errorMessages: {
        not_in_allowed_promotions:
          'package_deal.validation.allowed_promotions.not_in_allowed_promotions'
      }
    },
    {
      key: 'order_eligibility',
      label: 'package_deal.validation.order_eligibility.label',
      priority: 3,
      modes: [],
      entryModes: [],
      validate: async (order, _newsletterChoice, _promotionLineItem, onOrderDetails) => {
        return orderIsEligibleForPackageDeal(order, onOrderDetails)
      },
      errorMessages: {
        invalid_payment_status: 'package_deal.validation.order_eligibility.invalid_payment_status',
        order_contains_returned_items:
          'package_deal.validation.order_eligibility.order_contains_returned_items',
        already_has_package_deal:
          'package_deal.validation.order_eligibility.already_has_package_deal',
        discount_already_applied:
          'package_deal.validation.order_eligibility.discount_already_applied',
        line_items_do_not_meet_package_deal_requirements:
          'package_deal.validation.order_eligibility.line_items_do_not_meet_package_deal_requirements',
        validation_error: 'package_deal.validation.error'
      }
    }
  ]

  /**
   * Run all applicable validation rules for the current order
   * @param {Object} order - The current order to validate
   * @param {string} mode - The package deal mode
   * @param {boolean} newsletterChoice - Whether user agreed to newsletter
   * @param {boolean} onOrderDetails - Whether validation is from order details page
   * @returns {Promise<Object>} - { success: boolean, mode: string, results: Array }
   */
  async function validatePackageDealOrder(
    order,
    mode,
    newsletterChoice = false,
    onOrderDetails = false
  ) {
    try {
      // 1. Early return if no valid mode
      if (!mode) {
        return {
          success: false,
          mode: null,
          results: [],
          error: 'No valid package deal mode for this order'
        }
      }

      // Entry mode (default on Checkout)
      const entryMode = onOrderDetails ? 'orderDetails' : 'checkout'

      // 2. Get all rules
      const allRules = getValidationRules()

      // 3. Filter rules based on mode and entryMode
      const applicableRules = allRules.filter((rule) => {
        const modeMatches = rule.modes.length === 0 || rule.modes.includes(mode)
        const entryModeMatches = rule.entryModes.length === 0 || rule.entryModes.includes(entryMode)
        return modeMatches && entryModeMatches
      })

      // 4. Sort by priority
      const sortedRules = applicableRules.sort((a, b) => a.priority - b.priority)

      // 5. Get promotion line item for validation
      const promotionLineItem = getPromotionLineItem(order)?.item

      // 6. Run all validations in parallel
      const validationPromises = sortedRules.map(async (rule) => {
        try {
          const result = await rule.validate(
            order,
            newsletterChoice,
            promotionLineItem,
            onOrderDetails
          )
          return {
            key: rule.key,
            label: rule.label,
            priority: rule.priority,
            success: result.success,
            errorKey: result.errorKey,
            errorDetails: result.errorDetails,
            errorMessage: result.errorKey ? rule.errorMessages[result.errorKey] : null
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { validation_rule: rule.key }
          })
          return {
            key: rule.key,
            label: rule.label,
            priority: rule.priority,
            success: false,
            errorKey: 'validation_error',
            errorMessage: 'package_deal.validation.error'
          }
        }
      })

      const results = await Promise.all(validationPromises)

      // 7. Determine overall success
      const allPassed = results.every((r) => r.success)

      return {
        success: allPassed,
        mode,
        results
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { function: 'validatePackageDealOrder' }
      })
      throw error
    }
  }

  // ============================================================================
  // PUBLIC API - Initialization & Application
  // ============================================================================

  /**
   * Initialize package deal process
   * This is the entry point when user clicks "Add Package Deal"
   * Performs mode detection, cleanup, and validation
   *
   * @param {Object} order - The current order to initialize package deal for
   * @param {boolean} newsletterChoice - Whether user agreed to newsletter
   * @param {boolean} onOrderDetails - Whether initialization is from order details page
   * @returns {Promise<Object>} - { success: boolean, mode: string, results: Array, cleanupPerformed: boolean }
   */
  async function initPackageDeal(order, newsletterChoice = false, onOrderDetails = false) {
    try {
      let cleanupPerformed = false

      // 1. get mode
      const mode = getMode(order)

      // Early return if no valid mode
      if (!mode) {
        return {
          success: false,
          mode: null,
          results: [],
          cleanupPerformed: false,
          error: 'No valid package deal mode for this order'
        }
      }

      // 2. Perform any necessary cleanup based on mode
      // Example: Check for stale attachments, conflicting promotions, etc.
      try {
        // Check for orphaned influencer package deal attachments
        const influencerItem = getInfluencerPackageDealItem(order)
        const hasInfluencerLineItem = order.line_items?.some(influencerFilter)

        // If we have an attachment but no line item, clean it up
        if (influencerItem && !hasInfluencerLineItem) {
          await removeInfluencerPackageDealItem(order)
          cleanupPerformed = true
        }
      } catch (cleanupError) {
        // Log but don't fail initialization on cleanup errors
        Sentry.captureException(cleanupError, {
          tags: { function: 'initPackageDeal_cleanup' }
        })
      }

      // 3. Run validation
      const validationResult = await validatePackageDealOrder(
        order,
        mode,
        newsletterChoice,
        onOrderDetails
      )

      return {
        mode,
        ...validationResult,
        cleanupPerformed
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { function: 'initPackageDeal' }
      })
      throw error
    }
  }

  /**
   * Check if a promotion is an influencer promotion
   *
   * @param {Object} promotionLineItem - The promotion line item from the order
   * @returns {boolean} - True if promotion name includes 'influencer'
   */
  function isInfluencerPromotion(promotionLineItem) {
    if (!promotionLineItem?.item?.name) {
      return false
    }
    return promotionLineItem.item.name.toLowerCase().includes('influencer')
  }

  /**
   * Transform promotion line item to influencer coupon format
   * This converts a promotion line item to the format expected by updateInfluencerPromotion
   *
   * @param {Object} promotionLineItem - The promotion line item from the order
   * @param {Object} currentOrder - The current order object
   * @returns {Object} - Coupon object in the format expected by updateInfluencerPromotion
   *
   * @example
   * const coupon = transformPromotionToCoupon(promotionLineItem, currentOrder)
   * // Returns: {
   * //   code: 'INFLUENCER2024',
   * //   promotion_rule: {
   * //     promotion: {
   * //       name: 'Influencer Package Deal',
   * //       id: 'promo123',
   * //       type: 'flex_promotions'
   * //     }
   * //   }
   * // }
   */
  function transformPromotionToCoupon(promotionLineItem, currentOrder) {
    if (!promotionLineItem?.item) {
      return null
    }

    return {
      code: currentOrder.coupon_code || promotionLineItem.item.name,
      promotion_rule: {
        promotion: {
          name: promotionLineItem.item.name,
          id: promotionLineItem.item.id,
          type: promotionLineItem.item_type
        }
      }
    }
  }

  /**
   * Switch mode to influencer variant if promotion is influencer type
   *
   * @param {string} mode - The current package deal mode
   * @param {Object} promotionLineItem - The promotion line item to check
   * @returns {string} - Updated mode (influencer variant if applicable)
   *
   * @example
   * const mode = 'regular'
   * const updatedMode = switchToInfluencerModeIfNeeded(mode, promotionLineItem)
   * // Returns: 'regular_influencer' if promotion is influencer type
   */
  function switchToInfluencerModeIfNeeded(mode, promotionLineItem) {
    if (!isInfluencerPromotion(promotionLineItem)) {
      return mode
    }

    if (isInfluencerMode(mode)) {
      return mode // Already influencer mode
    }

    // Switch to influencer variant
    if (isInfluencerMode(mode)) {
      return mode // Already influencer mode
    } else if (isRegularMode(mode)) {
      return MODES.REGULAR_INFLUENCER
    } else if (isSingleWithCorrectionMode(mode)) {
      return MODES.SINGLE_WITH_CORRECTION_INFLUENCER
    } else if (isSingleWithoutCorrectionMode(mode)) {
      return MODES.SINGLE_WITHOUT_CORRECTION_INFLUENCER
    }
  }

  /**
   * If mode contains influencer, return PACKAGE_DEAL_INFLUENCER_TAG, else PACKAGE_DEAL_TAG
   *
   * @param {string} mode - The package deal mode (from MODES constant)
   * @returns {string} - Tag to add based on mode
   */
  function getTagToAddForMode(mode) {
    if (mode === MODES.SINGLE_INFLUENCER || mode === MODES.REGULAR_INFLUENCER) {
      return PACKAGE_DEAL_INFLUENCER_TAG
    }
    return PACKAGE_DEAL_TAG
  }

  /**
   * Remove a package deal from an order
   * Cleans up attachments (order links and influencer items) and tags
   */
  async function removePackageDeal(currentOrder, refresh = true) {
    try {
      if (refresh) {
        currentOrder = await cl.orders.retrieve(currentOrder.id, {
          include: ['tags', 'line_items', 'attachments'],
          fields: ['number', 'metadata', 'line_items', 'tags', 'attachments']
        })
      }

      // Remove influencer promotion attachment if present
      await removeAllPackageDealAttachments(currentOrder)

      // Update current order tags only
      const currentOrderUpdates = {
        id: currentOrder.id,
        tags: currentOrder.tags
      }

      return await handlePackageDealOnOrder(currentOrderUpdates)
    } catch (error) {
      Sentry.setContext('package-deal-error', {
        currentOrderId: currentOrder.id
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('removeRetroPackageDealFailed')
        return scope
      })

      throw error
    }
  }

  /**
   * Apply package deal based on mode
   *
   * This is the unified entry point for applying package deals after validation.
   * It automatically branches to the appropriate implementation based on the mode:
   * - Single mode (SINGLE, SINGLE_INFLUENCER): Calls setPackageDealOrderLinks
   * - Regular mode (REGULAR, REGULAR_INFLUENCER): Calls handlePackageDealOnOrder
   * - Automatically fetches and handles influencer promotions internally
   *
   * @param {string} mode - The package deal mode (from MODES constant)
   * @param {Object} params - Parameters for the application
   * @param {string} params.currentOrder - The current order object
   * @param {string} params.originOrder - The origin order object (required for single mode)
   * @returns {Promise<Object>} The updated order
   * @throws {Error} If mode is invalid or required parameters are missing
   *
   * @example
   * // Single mode
   * const order = await applyPackageDeal('single', {
   *   currentOrder: orderObj,
   *   originOrder: lastOrderObj
   * })
   *
   * @example
   * // Regular mode
   * const order = await applyPackageDeal('regular', {
   *   currentOrder: orderObj
   * })
   */
  async function applyPackageDeal(mode, params) {
    try {
      // Validate mode
      if (!mode || !Object.values(MODES).includes(mode)) {
        throw new Error(`Invalid mode: ${mode}. Must be one of: ${Object.values(MODES).join(', ')}`)
      }

      // Fetch promotion line item from the current order
      const promotionLineItem = getPromotionLineItem(params.currentOrder)

      // Check if we need to switch to influencer mode
      let finalMode = mode
      let influencerCoupon = null

      if (promotionLineItem) {
        // Check if promotion is influencer type and switch mode if needed
        finalMode = switchToInfluencerModeIfNeeded(mode, promotionLineItem)

        // If mode switched to influencer, transform promotion to coupon format
        if (isInfluencerPromotion(promotionLineItem)) {
          influencerCoupon = transformPromotionToCoupon(promotionLineItem, params.currentOrder)

          if (
            params.currentOrder?.coupon_code &&
            params.currentOrder?.fulfillment_status === 'unfulfilled'
          ) {
            // remove existing coupon code to prevent conflicts with influencer promotion
            await cl.orders.update({
              id: params.currentOrder.id,
              coupon_code: null,
              _refresh: true
            })
          }
        }
      }

      const isSingle = isSingleMode(finalMode)
      const isRegular = isRegularMode(finalMode)
      const tagToAdd = getTagToAddForMode(finalMode)

      if (isSingle) {
        // Single mode: apply retro package deal
        const { currentOrder, originOrder } = params

        if (!currentOrder || !originOrder) {
          throw new Error('Single mode requires both currentOrder and originOrder')
        }

        return await setPackageDealOrderLinks(currentOrder, originOrder, influencerCoupon, tagToAdd)
      } else if (isRegular) {
        // Regular mode with influencer promotion
        return await handlePackageDealOnOrder(
          { id: params.currentOrder.id, tags: params.currentOrder.tags },
          influencerCoupon,
          tagToAdd
        )
      }

      throw new Error(`Unsupported mode: ${finalMode}`)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { function: 'applyPackageDeal' },
        extra: { mode, params }
      })
      throw error
    }
  }

  // The adjustment amount is discount amount of all the other line items
  function calculatePackageDealAdjustmentAmount(order, selectedReturnItems, promotionItem) {
    if (!promotionItem) {
      return 0
    }

    // if we have a return status received and packageDealAdjustmentAmount set in metadata, we can trust that value as it was calculated at the moment of receiving the returned items and won't be affected by any further changes to the order (like additional returns or cancellations)
    const receivedReturn = order.returns?.find((returnItem) => returnItem.status === 'received')
    if (
      receivedReturn &&
      receivedReturn.metadata?.packageDealRefundData?.packageDealAdjustmentAmount
    ) {
      return parseFloat(receivedReturn.metadata.packageDealRefundData.packageDealAdjustmentAmount)
    }

    const otherLineItems = order.line_items
      .filter(
        (lineItem) => !selectedReturnItems.some((returnItem) => returnItem.id === lineItem.id)
      )
      .filter((lineItem) => {
        const isReturned = lineItem.return_line_items && lineItem.return_line_items.length > 0
        return !isReturned
      }) // only consider line items that are not already returned

    // on Retro the discount breakdown is only present on the capture transaction, not on the promotion line item, so we need to get the discount amount from the capture transaction instead
    if (promotionItem?.metadata?.discount_breakdown) {
      // if the PD is not fully refunded, we need to calculate the adjustment amount based on the discount breakdown of the other line items, otherwise we can just use the refund amount of the promotion item
      if (promotionItem?.refund_amount_float > 0) {
        const adjustmentAmount = otherLineItems.reduce((sum, lineItem) => {
          if (promotionItem?.metadata?.discount_breakdown[lineItem.id]) {
            promotionItem?.metadata?.discount_breakdown[lineItem.id]
            return (
              sum +
              Math.abs(
                Math.round(promotionItem?.metadata?.discount_breakdown[lineItem.id].cents) / 100
              )
            )
          }
          return sum
        }, 0)
        return adjustmentAmount
      } else {
        return promotionItem?.refund_amount_float
      }
    }

    const adjustmentAmount = otherLineItems.reduce((sum, lineItem) => {
      if (lineItem.discount_breakdown && lineItem.discount_breakdown[promotionItem.id]) {
        lineItem.discount_breakdown[promotionItem.id]
        return sum + Math.abs(Math.round(lineItem.discount_breakdown[promotionItem.id].cents) / 100)
      }
      return sum
    }, 0)

    return adjustmentAmount
  }

  function selectedItemsDoHavePackageDealPromotion(
    items,
    packageDealPromotionItem,
    retroPackageDealCapture
  ) {
    const hasPromotionBreakdown = Boolean(
      packageDealPromotionItem &&
        items.some(
          (item) => item.discount_breakdown && item.discount_breakdown[packageDealPromotionItem.id]
        )
    )

    const hasCaptureDiscount = Boolean(
      retroPackageDealCapture &&
        items.some((item) => {
          const discounted =
            retroPackageDealCapture.metadata?.discount_breakdown &&
            retroPackageDealCapture.metadata.discount_breakdown[item.id]
          return discounted
        })
    )

    return hasPromotionBreakdown || hasCaptureDiscount
  }

  /**
   * Only return an amount if the package deal discount is not yet refunded.
   * @param {*} order
   * @returns
   */
  function getPackageDealAdjustmentAmount(order) {
    if (order?.status === 'cancelled' || !doCalculatePackageDealAdjustmentAmount(order)) return 0
    const packageDealPromotionItem = getPackageDealDiscountLineItem(order)
    if (!packageDealPromotionItem) return 0
    const selectedReturnItems = []
    order.returns
      ?.filter((returnItem) => returnItem.status !== 'received')
      .forEach((returnItem) => {
        selectedReturnItems.push(...returnItem.return_line_items)
      })

    return calculatePackageDealAdjustmentAmount(
      order,
      selectedReturnItems,
      packageDealPromotionItem
    )
  }

  /**
   * Determines if a package deal discount should be refunded (clawed back) when items are returned.
   *
   * This function evaluates whether returning selected items invalidates a package deal and calculates
   * the discount amount to refund. It handles three scenarios:
   *
   * 1. Self-referencing linked order check (prevents infinite recursion)
   * 2. Package deal applied directly on this order (checkout or retro)
   * 3. Package deal applied on a linked order (retro single mode)
   *
   * Flow:
   * - Check if return makes order ineligible for package deal
   * - Calculate refund amount from promotion line item or retro capture
   * - Find suitable capture transaction to refund against
   * - For linked orders, recursively check the linked order
   *
   * @param {Object} order - The order object with transactions, line_items, attachments, and tags
   * @param {Array} selectedReturnItems - Array of line items being returned
   * @param {Set} processedOrders - Set of already processed order IDs (prevents infinite recursion)
   * @param {boolean} isOrderLinkCheck - Internal flag to prevent self-referencing when checking linked orders
   *
   * @returns {Promise<Object>} Refund determination result
   *
   */
  async function determinePackageDealRefund(
    order,
    selectedReturnItems,
    processedOrders = new Set(),
    isOrderLinkCheck = false
  ) {
    if (processedOrders.has(order.id)) {
      return { success: false }
    }
    processedOrders.add(order.id)

    const result = {
      success: false,
      // refundAmountFloat: 0,
      packageDealAdjustmentAmount: 0,
      retroCapture: null,
      // captureToRefund: {},
      linkedOrderNumber: null,
      linkedAttachment: null,
      promotionItem: {
        type: 'package_deal',
        applied_order: null,
        promotion_id: null,
        promotion_name: null,
        total_amount_float: 0
      }
    }

    try {
      // PD claw-back only makes sense once the order has actually been paid.
      // Cancelling an unpaid fulfillment on a partially_paid order must not
      // strip the PD discount from already-paid line items (TECH-658).
      if (!['paid', 'partially_refunded'].includes(order.payment_status)) {
        return result
      }

      const packageDealPromotionItem = getPackageDealDiscountLineItem(order)
      const retroPackageDealCapture = getRetroPackageDealCapture(order)
      const linkedOrderLink = getPackageDealOrderLink(order)
      result.linkedAttachment = linkedOrderLink

      // CASE 1: Package deal applied directly on this order
      // Either as a promotion line item (checkout) or as a retro capture (order details)
      // Only refund if returning items makes the order ineligible for package deal.
      // The current order's PD always takes priority over any linked order's PD —
      // a stale or abandoned link must not prevent this order's PD from being refunded.
      if (packageDealPromotionItem || retroPackageDealCapture) {
        if (!isOrderLinkCheck) {
          const hasPromotionBreakdown = selectedItemsDoHavePackageDealPromotion(
            selectedReturnItems,
            packageDealPromotionItem,
            retroPackageDealCapture
          )

          if (!hasPromotionBreakdown) {
            return result
          }
        }
        if (packageDealPromotionItem) {
          // Skip if a PD storno is pending OR the PD has already been cancelled
          // (refunded). The latter catches the 3rd+ subsequent returns where the most-recent
          // received return has no PD data, so getPackageDealStornoAmount drops back to 0
          // and would otherwise re-trigger the first PD return's adjustment amount.
          if (getPackageDealStornoAmount(order) > 0 || packageDealCancelled(order)) {
            return result
          }

          // Store promotion details for refund metadata -> for better tracking
          result.promotionItem.applied_order = order.number
          result.promotionItem.promotion_id = packageDealPromotionItem.item.id
          result.promotionItem.promotion_name = packageDealPromotionItem.item.name
          result.promotionItem.total_amount_float =
            packageDealPromotionItem.total_amount_float - order.adjustment_amount_float
          result.packageDealAdjustmentAmount = calculatePackageDealAdjustmentAmount(
            order,
            selectedReturnItems,
            packageDealPromotionItem,
            false // no need to verify promotion breakdown again since we already know the selected items are discounted by this promotion
          )

          // result.packageDealAdjustmentAmount === packageDealPromotionItem.total_amount_float do nothing as all is returned
          if (result.packageDealAdjustmentAmount === packageDealPromotionItem.total_amount_float) {
            result.packageDealAdjustmentAmount = 0
            return result
          }
        } else if (retroPackageDealCapture) {
          result.retroCapture = retroPackageDealCapture
          result.promotionItem.applied_order = order.number
          result.promotionItem.promotion_id = retroPackageDealCapture.metadata.promotion_id || null
          result.promotionItem.promotion_name =
            retroPackageDealCapture.metadata.promotion_name || null
          result.promotionItem.total_amount_float = retroPackageDealCapture.amount_float || 0
          result.packageDealAdjustmentAmount = calculatePackageDealAdjustmentAmount(
            order,
            selectedReturnItems,
            retroPackageDealCapture,
            false // no need to verify promotion breakdown again since we already know the selected items are discounted by this capture
          )

          if (
            result.packageDealAdjustmentAmount === retroPackageDealCapture.amount_float ||
            retroPackageDealCapture.refund_balance_float === 0
          ) {
            result.packageDealAdjustmentAmount = 0
            return result
          }
        }
      }

      // Find a suitable capture transaction to refund against
      // Must have sufficient refund balance and be a supported payment method
      if (result.packageDealAdjustmentAmount) {
        const capture = order.transactions.find(
          (tx) =>
            tx.type === 'captures' &&
            tx.refund_balance_float >= result.packageDealAdjustmentAmount &&
            ['CREDIT_CARD', 'CASH', 'GIFT_CARD'].includes(tx.metadata.payment_method)
        )

        if (capture) {
          result.success = true
        }
      }
    } catch (error) {
      Sentry.setContext('package-deal-error', {
        orderNumber: order.number,
        selectedReturnItems
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('determinePackageDealRefundFailed')
        return scope
      })
    } finally {
      return result
    }
  }

  /**
   * Re-Calculate the line item discount amounts on a storno PD and marks the PD breakdown as virtually cancelled so that it can be ignored in the UI,
   * without actually removing the breakdown which is needed for accurate refund amount calculation in case of an exchange cancellation scenario.
   *
   * @param {*} lineItem
   * @param {*} packageDealDiscountLineItem
   * @returns
   */
  function excludePackageDealFromLineItemDiscount(lineItem, packageDealDiscountLineItem = null) {
    if (packageDealDiscountLineItem) {
      const breakdownsFiltered = Object.entries(lineItem.discount_breakdown || {}).filter(
        ([key, _breakdown]) => key !== packageDealDiscountLineItem.id
      )
      const totalDiscountCents = breakdownsFiltered.reduce(
        (sum, [_, breakdown]) => sum + Math.abs(breakdown.cents),
        0
      )
      lineItem.discount_cents = -totalDiscountCents
      lineItem.discount_float = -totalDiscountCents / 100

      // Mark package deal breakdown as virtually cancelled
      if (
        lineItem.discount_breakdown &&
        lineItem.discount_breakdown[packageDealDiscountLineItem.id]
      ) {
        lineItem.discount_breakdown[packageDealDiscountLineItem.id].virtuallyCancelled = true
      }
    }

    return lineItem
  }

  function adjustDiscountsOnStorno(order) {
    if (order.status === 'cancelled') return order
    try {
      let packageDealDiscountLineItem
      if (packageDealCancelled(order)) {
        packageDealDiscountLineItem = getPackageDealDiscountLineItem(order)
      }

      order.line_items = order.line_items.map((lineItem) =>
        excludePackageDealFromLineItemDiscount(lineItem, packageDealDiscountLineItem)
      )
    } catch (error) {
      Sentry.setContext('package-deal-error', {
        orderId: order.id
      })
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('adjustDiscountsOnStornoFailed')
        return scope
      })
    } finally {
      return order
    }
  }

  /**
   * Cleans up stale package deal data on a copied (target) order.
   * Called after an order copy completes. Removes the transferred PD link
   * attachments, removes PD tags from the target, and shows a warning
   * notification if a package deal was detected.
   *
   * @param {string} targetOrderId - ID of the newly created order copy
   * @param {Object} sourceOrder - The source order (needs .number and .line_items)
   * @param {Array} attachmentsToCopy - Attachments already moved to the target order
   */
  async function cleanPackageDealOnOrderCopy(targetOrderId, sourceOrder, attachmentsToCopy) {
    const pdLinkAttachment = attachmentsToCopy.find(
      (att) =>
        att.reference_origin === PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN &&
        att.metadata?.role === 'current'
    )
    const hasPackageDealLineItem = sourceOrder?.line_items?.some((li) =>
      li.name?.startsWith('Package Deal')
    )

    if (!pdLinkAttachment && !hasPackageDealLineItem) return

    if (pdLinkAttachment) {
      const originAttachments = await cl.attachments.list({
        filters: {
          reference_eq: sourceOrder.number,
          reference_origin_start: `${PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN}-linked`
        }
      })
      await Promise.all([
        cl.attachments.delete(pdLinkAttachment.id),
        ...originAttachments.map((att) => cl.attachments.delete(att.id))
      ])
    }

    const targetOrderWithTags = await cl.orders.retrieve(targetOrderId, {
      include: ['tags'],
      fields: { orders: ['id', 'tags'] }
    })
    if (targetOrderWithTags.tags?.some((tag) => tag.name === PACKAGE_DEAL_TAG)) {
      await cl.orders._remove_tags(targetOrderId, PACKAGE_DEAL_TAG)
    }
    if (targetOrderWithTags.tags?.some((tag) => tag.name === PACKAGE_DEAL_INFLUENCER_TAG)) {
      await cl.orders._remove_tags(targetOrderId, PACKAGE_DEAL_INFLUENCER_TAG)
    }

    showNotification({
      message: t('package_deal.notification.removed_on_order_copy'),
      variant: 'warning'
    })
  }

  /**
   * Freeze the package deal order link at cart approval time.
   *
   * When a single-mode PD cart is approved, the reverse attachment on the origin
   * order may point to an abandoned/stale cart from a previous session. This
   * function creates the authoritative reverse link on the origin order so that
   * subsequent return lookups use a snapshot that was valid at approval, not a
   * live link that may have drifted.
   *
   * Non-fatal: errors are captured in Sentry but do not block the approval flow.
   *
   * @param {Object} approvedOrder - The freshly approved order (must include attachments)
   */
  async function freezePackageDealLinkOnApprove(approvedOrder) {
    const orderLink = getPackageDealOrderLink(approvedOrder)

    if (
      !orderLink ||
      orderLink.metadata?.role !== 'current' ||
      !orderLink.metadata?.linked_order_id
    ) {
      return
    }

    try {
      // Check whether the correct reverse link already exists on the origin order
      const existingReverseLinks = await cl.attachments.list({
        filters: {
          reference_eq: approvedOrder.number,
          reference_origin_start: `${PACKAGE_DEAL_ORDER_LINK_REFERENCE_ORIGIN}-linked`,
          metadata_jcont: { role: 'origin' }
        }
      })

      if (existingReverseLinks.length > 0) {
        return
      }

      // Stamp the reverse link on the origin order using the metadata frozen at PD-apply time
      const originOrderStub = {
        id: orderLink.metadata.linked_order_id,
        number: orderLink.metadata.linked_order_number
      }
      await createPackageDealOrderLink(originOrderStub, approvedOrder, 'origin')
    } catch (error) {
      Sentry.captureException(error, (scope) => {
        scope.setTransactionName('freezePackageDealLinkOnApproveFailed')
        return scope
      })
    }
  }

  return {
    buildVirtualDiscountLineItem,
    packageDealCancelled,
    packageDealApplied,
    promotionPackageDealToggle,
    getPackageDealName,
    getCustomerLastOrders,
    getCustomerLastOrder,
    getPackageDealOrderLink,
    getPackageDealDiscountLineItem,
    getRetroPackageDealCapture,
    showRemovePackageDealButton,
    showPackageDealDialog,
    getPackageDealPromotionRule,
    getMode,
    isSingleMode,
    isSingleWithoutCorrectionMode,
    isSingleWithCorrectionMode,
    isRegularMode,
    isInfluencerMode,
    initPackageDeal,
    removePackageDeal,
    applyPackageDeal,
    getPackageDealAdjustmentAmount,
    determinePackageDealRefund,
    getPackageDealStornoAmount,
    adjustDiscountsOnStorno,
    excludePackageDealFromLineItemDiscount,
    isAllowedRetroPromotion,
    PACKAGE_DEAL_ALLOWED_TAG,
    cleanPackageDealOnOrderCopy,
    freezePackageDealLinkOnApprove,
    ALLOWED_RETRO_PROMOTIONS
  }
}
